import { TelemetryEvent, TelemetryMeasurement, trackEvent, trackMeasurement } from './telemetry';

const STORAGE_SAMPLE_INTERVAL_MS = 5 * 60 * 1000;

type NetworkInformation = {
	effectiveType?: string;
	downlink?: number;
	rtt?: number;
	saveData?: boolean;
	addEventListener?: (type: string, listener: () => void) => void;
};

function getConnection(): NetworkInformation | undefined {
	return (navigator as Navigator & { connection?: NetworkInformation }).connection;
}

function reportConnectivity(reason: string): void {
	const connection = getConnection();

	trackEvent(TelemetryEvent.ConnectivityChanged, {
		reason,
		online: navigator.onLine,
		effectiveType: connection?.effectiveType,
		downlinkMbps: connection?.downlink,
		rttMs: connection?.rtt,
		saveData: connection?.saveData,
	});
}

async function sampleStorage(): Promise<void> {
	if (!navigator.storage?.estimate) {
		return;
	}

	try {
		const { usage, quota } = await navigator.storage.estimate();
		if (usage === undefined || quota === undefined || quota === 0) {
			return;
		}

		trackMeasurement(TelemetryMeasurement.StorageUsage, {
			usageBytes: usage,
			quotaBytes: quota,
			usedPct: Math.round((usage / quota) * 10000) / 100,
		});
	} catch {
		console.warn('Telemetry cannot break app');
	}
}

/**
 * Connectivity and storage telemetry. Deliberately React-free so it can start together with
 * Faro, before the app tree mounts — a technician losing connectivity during bootstrap is
 * exactly the case we could not see before.
 */
export default function instrumentDevice(): void {
	window.addEventListener('online', () => reportConnectivity('online'));
	window.addEventListener('offline', () => reportConnectivity('offline'));
	getConnection()?.addEventListener?.('change', () => reportConnectivity('connection-change'));

	reportConnectivity('startup');

	sampleStorage();
	window.setInterval(sampleStorage, STORAGE_SAMPLE_INTERVAL_MS);
}
